import { useRef } from "react";
import { useReveal } from "@/lib/animations";
import { ASSETS } from "@/lib/data";

const STEPS = [
  {
    n: "01",
    title: "Listen",
    text:
      "We start with your story — your family, your income, your obligations, your fears, and the future you actually want. No product talk yet.",
  },
  {
    n: "02",
    title: "Strategize",
    text:
      "We model your numbers across multiple strategies and carriers, then build the plan that protects you in the worst case and rewards you in the best.",
  },
  {
    n: "03",
    title: "Implement",
    text:
      "We handle the paperwork, the underwriting, and the coordination with carriers. You sign once and we run the process to the finish line.",
  },
  {
    n: "04",
    title: "Steward",
    text:
      "Markets shift. Tax law changes. Families grow. We meet with you regularly to make sure the plan you bought is still the plan you need.",
  },
];

export const ProcessSection = () => {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section
      ref={ref}
      data-testid="process-section"
      className="bg-[#F0EBE0] grain relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <p
            data-reveal
            className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4"
          >
            How we work
          </p>
          <h2
            data-reveal
            className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-[#0B132B]"
          >
            A deliberate process,{" "}
            <span className="text-gradient-breeze">designed around you</span>.
          </h2>
          <p
            data-reveal
            className="mt-6 text-[#334155] font-light leading-relaxed max-w-md"
          >
            We don't push products. We build plans. Every engagement follows the
            same disciplined process — the kind you'd expect from a firm
            managing generations of wealth, not chasing the next sale.
          </p>
          <div data-reveal className="mt-10 hidden lg:block">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-[#0B132B]/15">
              <img
                src={ASSETS.familyField}
                alt="A family walking together"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>

        <ol className="lg:col-span-7 space-y-5">
          {STEPS.map((s, i) => (
            <li
              key={s.n}
              data-reveal
              data-reveal-delay={i * 0.06}
              data-testid={`process-step-${s.n}`}
              className="group relative rounded-3xl bg-white border border-[#0B132B]/5 shadow-sm p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-6 sm:gap-10">
                <div className="font-heading font-black text-5xl sm:text-6xl text-gradient-breeze leading-none shrink-0">
                  {s.n}
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-black text-2xl tracking-tight text-[#0B132B]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[#334155] font-light leading-relaxed">
                    {s.text}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
